import * as React from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { Button, Tooltip } from 'antd';

import { useQuery } from '@tanstack/react-query';
import TopPage from 'v2/components/Shared/TopPage';
import type { IBankAccount } from 'v2/interfaces/financial';
import bankDetailsService from 'v2/services/bankDetails';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import fidcStatusService from 'v2/services/fidic';
import financialService from 'v2/services/financial';

import { AppRedirectModal } from './AppRedirectModal';
import { BankDetailsTable } from './BankDetailsTable';
import { NewAccountWarningModal } from './NewAccountWarningModal';

const MESSAGE_NO_COMMISSION =
  'Para realizar o cadastro de uma conta bancária, é obrigatório ter realizado pelo menos uma venda em sua conta Eduzz.';

function minutesToMiliseconds(minutes: number) {
  return minutes * (60 * 1000);
}

function BankDetails() {
  const [isOpenNewAccountWarningModal, setIsOpenNewAccountWarningModal] = React.useState(false);
  const [isOpenAppRedirectModal, setIsOpenAppRedirectModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [bankDetails, setBankDetails] = React.useState<IBankAccount[]>(null);

  const [isFidcAuthorized] = useObservable(() => fidcStatusService.checkFidcApi(user?.id), [user?.id]);

  const userHasCommissionsData = useQuery({
    queryKey: ['userHasCommissions'],
    queryFn: () => financialService.checkHasCommissions(),
    staleTime: minutesToMiliseconds(10),
    gcTime: minutesToMiliseconds(15)
  });

  const userHasCommissions = userHasCommissionsData.data;
  const shouldDisableNewAccountBtn = !isFidcAuthorized?.isAuthorized || !userHasCommissions;

  const toggleOpenNewAccountWarningModal = () => {
    setIsOpenNewAccountWarningModal(isOpen => !isOpen);
  };

  const toggleOpenAppRedirectModal = () => {
    setIsOpenAppRedirectModal(isOpen => !isOpen);
  };

  const loadBankDetails = async () => {
    try {
      await bankDetailsService.listBankDetails().then(details => setBankDetails(details));
    } catch (error) {
      toast.error(error);
    }

    setIsLoading(false);
  };

  function getDisableNewAccountMessage() {
    if (!userHasCommissions) return MESSAGE_NO_COMMISSION;
    return '';
  }

  function NewBankAccountButton() {
    return (
      <Tooltip placement='left' title={getDisableNewAccountMessage()}>
        <div>
          <Button id='new-bank-account' onClick={toggleOpenAppRedirectModal} disabled={shouldDisableNewAccountBtn}>
            Cadastrar nova conta
          </Button>
        </div>
      </Tooltip>
    );
  }

  React.useEffect(() => {
    loadBankDetails();
  }, []);

  return (
    <div className='font-sans'>
      <style>
        {`#main-content {
          max-width: 1900px;
          margin: 0 auto;
        }`}
      </style>
      <TopPage title='Contas bancárias' additionalContent={<NewBankAccountButton />} />
      <BankDetailsTable
        businessName={user?.business_name}
        isFidcAuthorized={isFidcAuthorized?.isAuthorized}
        bankDetails={bankDetails}
        isLoading={isLoading}
        loadBankDetails={loadBankDetails}
      />
      <NewAccountWarningModal isOpen={isOpenNewAccountWarningModal} setClose={toggleOpenNewAccountWarningModal} />
      <AppRedirectModal isOpen={isOpenAppRedirectModal} setClose={toggleOpenAppRedirectModal} />
    </div>
  );
}

export { BankDetails };
